import '@/lib/i18n'
import { changeLanguage as setI18nLanguage } from 'i18next'
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import {
  ensureLanguageResources,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '@/lib/i18n'
import { storage } from '@/lib/storage'

interface I18nState {
  locale: SupportedLanguage
  changeLanguage: (lang: SupportedLanguage) => Promise<void>
}

const I18nContext = createContext<I18nState | null>(null)

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocale] = useState<SupportedLanguage>('es')

  useEffect(() => {
    ;(async () => {
      // 1. Try stored preference
      const stored = await storage.get(LANGUAGE_STORAGE_KEY)
      if (stored && (SUPPORTED_LANGUAGES as readonly string[]).includes(stored)) {
        const lang = stored as SupportedLanguage
        await ensureLanguageResources(lang)
        await setI18nLanguage(lang)
        if (typeof document !== 'undefined') {
          document.documentElement.lang = lang
        }
        setLocale(lang)
        return
      }
      // 2. Fall back to app default
      const lang: SupportedLanguage = 'es'
      await setI18nLanguage(lang)
      if (typeof document !== 'undefined') {
        document.documentElement.lang = lang
      }
      setLocale(lang)
    })()
  }, [])

  const changeLanguage = useCallback(async (lang: SupportedLanguage) => {
    await ensureLanguageResources(lang)
    await setI18nLanguage(lang)
    await storage.set(LANGUAGE_STORAGE_KEY, lang)
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang
    }
    setLocale(lang)
  }, [])

  return <I18nContext.Provider value={{ locale, changeLanguage }}>{children}</I18nContext.Provider>
}

export function useI18n() {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within I18nProvider')
  return ctx
}
